import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { IconCopy2Fill18 } from "nucleo-ui-fill-18"
import {
	IconPen2Outline18 as Edit2,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { BrandfetchData } from "@productlane/api/src/browser"
import { getHostname } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Alert,
	AlertDialog,
	AttioIcon,
	Button,
	Empty,
	HubspotIcon,
	IconButton,
	// LinearIcon,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import EmptyCompany from "@/assets/empty-states/empty-company.png"
import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import {
	DetailScreenEditor,
	DetailScreenLayout,
	DetailThreadsList,
} from "@/components/detail-screen"
import { DetailScreenWithConnectors } from "@/components/detail-screen/custom-connectors-sidebar"
import { DetailUpvotesList } from "@/components/detail-screen/detail-upvotes-list"
import { useDetailListState } from "@/components/detail-screen/use-detail-list-state"
import { GroupedIssuesProjectsTable } from "@/components/issue-or-project-items/grouped-issues-projects-table"
import { MetaInfo } from "@/components/meta-info"
import { SubscribeButton } from "@/components/notifications/subscribe-button"
import { RoleCheck } from "@/components/shared/role-check"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useBackNavigation, useSetBackNavigation } from "@/lib/back-navigation"
import { useCategorizedIssues } from "@/lib/issues"
import { useCategorizedProjects } from "@/lib/projects"
import { areQueriesSettled, isQuerySettled } from "@/lib/query-settled"
import { useCmdkState } from "@/lib/states/cmdk-state"
import { useFilteredIssues, useFilteredProjects } from "@/lib/use-team-filter"
import { useRole } from "@/lib/useRole"
import { useTrial } from "@/lib/useTrial"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { HowItWorksButton } from "@/screens/settings/settings-switches/how-it-works-button"
import { SupportPortalSettingsContent } from "@/screens/settings/support-portal-settings-content"
import { CompanyContactSelect } from "../contacts/company-contact-select"
import { ContactTag } from "../contacts/contact-tag"
import { CreateNewThreadButton } from "../inbox/create-new-thread-button"
import { DetailBreadcrumb, DetailTopBar } from "../page-header"
import { PublishPortalPageButton } from "../portal/publish-portal-page-button"
import { CompanyRequestPicker } from "../support-portal/company-request-picker"
import { PortalRequestsPreview } from "../support-portal/portal-requests-preview"
import { CustomerProperties } from "../threads/components/right-bar/customer-properties"
import { CompanyLogo } from "./company-logo"
import { CompanyNameInput } from "./company-name-input"
import { MergeCompanyDialog } from "./merge-company-dialog"
import { useAdjacentCompanies } from "./sorting/use-adjacent-companies"
import { UpdateCustomerDialog } from "./update-customer-dialog"

// const fullConfig = resolveConfig(tailwindConfig)

export const CustomersDetailScreen = ({
	companyId,
	parentLabel = "Customers",
	parentPath = "/customers",
	isSupportPortalPreview = false,
}: {
	companyId?: string
	parentLabel?: string
	parentPath?: string
	isSupportPortalPreview?: boolean
} = {}) => {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { data: workspaceInfo } = api.workspace.info.useQuery()
	const navigate = useNavigate()
	const params = useParams({ strict: false })
	const id = companyId ?? params.id!
	useSetBackNavigation({
		type: "customers-detail",
		url: `${parentPath}/${id}`,
	})

	const [company, companyResult] = useQuery(queries.companyDetails(id))

	// Contacts pointing here as their primary plus everyone linked to this
	// company, since one person can work with several customers.
	const companyContacts = useMemo(() => {
		const primary = company?.contacts ?? []
		const linked = (company?.contactLinks ?? [])
			.map((link) => link.contact)
			.filter((c): c is NonNullable<typeof c> => !!c)
		return [...new Map([...primary, ...linked].map((c) => [c.id, c])).values()]
	}, [company?.contacts, company?.contactLinks])
	const [projectsRaw, projectsResult] = useQuery(queries.projectsForCompany(id))
	const [issuesRaw, issuesResult] = useQuery(queries.issuesForCompany(id))
	const threadsState = useDetailListState()
	const upvotesState = useDetailListState()

	// Filter by selected teams
	const projects = useFilteredProjects(projectsRaw)
	const issues = useFilteredIssues(issuesRaw)

	const categorizedIssues = useCategorizedIssues(issues)
	const categorizedProjects = useCategorizedProjects(projects)

	const [isConfirmDeleteOpen, setConfirmDelete] = useToggle(false)
	const [isUpdateCompanyOpen, setIsUpdateCompanyOpen] = useToggle(false)
	const [isMergeOpen, setIsMergeOpen] = useToggle(false)
	const { mergeCompanyWithId, clearMergeCompanyDialog } = useCmdkState()
	useEffect(() => {
		if (mergeCompanyWithId) setIsMergeOpen(true)
	}, [mergeCompanyWithId, setIsMergeOpen])

	const [tab, setTab] = useState<"requests" | "people" | "upvotes">("requests")

	const { goBack } = useBackNavigation()
	const close = useCallback(
		(shortcut = false) => {
			if (company) {
				goBack(parentPath)
				analytics.track("company-close", { id: company.id, shortcut })
			}
		},
		[company, goBack, parentPath],
	)
	useShortcut(
		"esc",
		() => {
			close(true)
		},
		{ enabled: !isConfirmDeleteOpen },
	)
	const { nextId, previousId } = useAdjacentCompanies(
		companyId ? null : company,
	)
	useShortcut(
		"j",
		() => {
			if (nextId)
				void navigate({
					to: "/customers/$id",
					params: { id: nextId },
					replace: true,
				})
		},
		{ enabled: !!nextId && !isConfirmDeleteOpen },
	)
	useShortcut(
		"k",
		() => {
			if (previousId)
				void navigate({
					to: "/customers/$id",
					params: { id: previousId },
					replace: true,
				})
		},
		{ enabled: !!previousId && !isConfirmDeleteOpen },
	)
	const { role } = useRole()
	const { hasTrialEnded } = useTrial()
	useShortcut(
		"meta+backspace, ctrl+backspace",
		() => {
			analytics.track("open-delete-company-dialog", {
				id: company?.id,
				shortcut: true,
			})
			setConfirmDelete(true)
		},
		{ enabled: role !== "VIEWER" },
	)
	useShortcut(
		"e",
		() => {
			if (company) {
				setIsUpdateCompanyOpen(true)
				analytics.track("open-update-company-dialog", {
					id: company.id,
					shortcut: true,
				})
			}
		},
		{ preventDefault: true, enabled: role !== "VIEWER" },
	)

	const isEmptyState =
		areQueriesSettled(projectsResult, issuesResult) &&
		threadsState.isSettled &&
		upvotesState.isSettled &&
		Object.values(categorizedProjects).every((arr) => arr.length === 0) &&
		Object.values(categorizedIssues).every((arr) => arr.length === 0) &&
		threadsState.size === 0 &&
		upvotesState.size === 0

	if (!company) {
		if (!isQuerySettled(companyResult)) return null
		return <EmptyCustomersDetailsScreen />
	}

	return (
		<>
			<MetaInfo title="Customer Detail" />
			<AlertDialog
				isOpen={isConfirmDeleteOpen}
				onOpenChange={setConfirmDelete}
				confirmText="Delete"
				title="Are you sure?"
				description="Do you really want to delete this customer?"
				onCancel={() => {
					setConfirmDelete(false)
				}}
				onConfirm={async () => {
					void navigate({ href: parentPath, replace: true })
					await mutateOk(zero.mutate(mutators.companies.delete({ id })))
				}}
			/>
			<UpdateCustomerDialog
				isOpen={isUpdateCompanyOpen}
				onOpenChange={setIsUpdateCompanyOpen}
				values={{
					id: company.id,
					domains: company.domains || [],
					name: company.name,
				}}
			/>
			<MergeCompanyDialog
				companyId={company.id}
				isOpen={isMergeOpen}
				onOpenChange={(open) => {
					setIsMergeOpen(open)
					if (!open) clearMergeCompanyDialog()
				}}
				initialWithId={mergeCompanyWithId}
			/>
			<DetailScreenWithConnectors
				target={{ type: "company", id: company.id }}
				enabled={!isSupportPortalPreview}
			>
				<DetailTopBar
					className="justify-between"
					clearsNavToggle={!isSupportPortalPreview}
				>
					<div className="flex min-w-0 items-center gap-1">
						<DetailBreadcrumb
							parentLabel={parentLabel}
							onParentClick={() => close()}
							currentIcon={
								<CompanyLogo
									brandfetchData={null}
									fallback={company.name}
									linearLogoUrl={company.logoUrl ?? undefined}
									size="xxs"
								/>
							}
							currentLabel={company.name}
						/>
						<RoleCheck not={["VIEWER"]}>
							<DropdownMenu
								trigger={
									<IconButton
										Icon={MoreVertical}
										variant="ghost"
										iconClassName="fill-current"
									/>
								}
								contentProps={{ side: "bottom", align: "start" }}
							>
								<DropdownMenuGroup>
									<DropdownMenuItem
										Icon={Edit2}
										shortcut="E"
										onClick={() => {
											setIsUpdateCompanyOpen(true)
											analytics.track("open-update-company-dialog", {
												id: company.id,
												shortcut: false,
											})
										}}
									>
										Edit
									</DropdownMenuItem>
									{company.attioId && workspaceInfo?.attioWorkspaceSlug && (
										<DropdownMenuItem
											Icon={AttioIcon}
											onClick={() => {
												analytics.track("open-company-in-attio", {
													id: company.id,
												})
												window.open(
													`https://app.attio.com/${workspaceInfo.attioWorkspaceSlug}/company/${company.attioId}`,
													"_blank",
												)
											}}
										>
											Open in Attio
										</DropdownMenuItem>
									)}
									{company.hubspotId && workspaceInfo?.hubspotPortalId && (
										<DropdownMenuItem
											Icon={HubspotIcon}
											onClick={() => {
												analytics.track("open-company-in-hubspot", {
													id: company.id,
												})
												window.open(
													`https://app.hubspot.com/contacts/${workspaceInfo.hubspotPortalId}/record/0-2/${company.hubspotId}`,
													"_blank",
												)
											}}
										>
											Open in HubSpot
										</DropdownMenuItem>
									)}
									<DropdownMenuItem
										Icon={IconCopy2Fill18}
										onClick={() => {
											setIsMergeOpen(true)
											analytics.track("open-merge-company-dialog", {
												id: company.id,
											})
										}}
									>
										Merge
									</DropdownMenuItem>
									<DropdownMenuItem
										Icon={Trash}
										shortcut={`${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`}
										onClick={() => {
											setConfirmDelete(true)
											analytics.track("open-delete-company-dialog", {
												id: company.id,
												shortcut: false,
											})
										}}
									>
										Delete
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenu>
						</RoleCheck>
					</div>
					{isSupportPortalPreview && workspace ? (
						<PublishPortalPageButton
							content={<SupportPortalSettingsContent />}
							isPublished={workspace.customerPortalSettings?.isEnabled || false}
							url={`https://${workspace.domain === "hello" ? "productlane.com" : getHostname(workspace)}/support`}
							name="support-portal"
						/>
					) : (
						<div className="flex items-center gap-1.5">
							<RoleCheck not={["VIEWER"]}>
								<CreateNewThreadButton
									disabled={hasTrialEnded}
									companyId={company.id}
									withLabel
								/>
							</RoleCheck>
							<SubscribeButton entityType="company" entityId={company.id} />
						</div>
					)}
				</DetailTopBar>
				<DetailScreenLayout>
					<DetailScreenEditor isPadded={false}>
						<div className={cn("flex items-center gap-2 px-6 py-10 pb-6")}>
							<CompanyLogo
								brandfetchData={
									company.companyMeta
										?.brandfetchData as unknown as BrandfetchData
								}
								fallback={company.name}
								linearLogoUrl={company?.logoUrl || undefined}
								size="lg"
							/>
							<div className="min-w-0 flex-1">
								<CompanyNameInput companyId={company.id} name={company.name} />
								{company.domains[0] && (
									<Tooltip label={company.domains.join(", ")} side="bottom">
										<a
											href={
												company.domains[0].startsWith("http")
													? company.domains[0]
													: `https://${company.domains[0]}`
											}
											target="_blank"
											rel="noreferrer"
											className="flex !cursor-default flex-row items-baseline justify-start gap-1 pl-2 text-sm text-secondary hover:text-tertiary"
										>
											{company.domains[0]}
											{company.domains.length > 1 && (
												<div className="text-sm">
													+{company.domains.length - 1}
												</div>
											)}
										</a>
									</Tooltip>
								)}
							</div>
						</div>

						{!!company && !isSupportPortalPreview && (
							<div className="px-6">
								<CustomerProperties variant="row" company={company} />
							</div>
						)}

						<div className="my-6 flex w-full items-center justify-center px-6">
							<div className="w-full border-t border-gray-200 dark:border-gray-700/80" />
						</div>

						<Tabs
							defaultValue="requests"
							value={tab}
							onValueChange={(value) => {
								if (
									value === "requests" ||
									value === "people" ||
									value === "upvotes"
								) {
									setTab(value)
								}
							}}
						>
							<div className="mx-6 mt-4 flex items-center justify-between">
								<TabsList>
									<TabsTrigger value="requests">Requests</TabsTrigger>
									<TabsTrigger value="people">Contacts</TabsTrigger>
									{!isSupportPortalPreview && (
										<TabsTrigger value="upvotes">Upvotes</TabsTrigger>
									)}
								</TabsList>
								<RoleCheck not={["VIEWER"]}>
									{tab === "requests" && isSupportPortalPreview && (
										<CompanyRequestPicker companyId={id} />
									)}
									{tab === "people" && (
										<CompanyContactSelect
											contactIds={companyContacts.map((c) => c.id)}
											onSelect={async (contact) => {
												if (
													companyContacts.map((c) => c.id).includes(contact.id)
												) {
													// remove
													await mutateOk(
														zero.mutate(
															mutators.contacts.unlinkCompany({
																contactId: contact.id,
																companyId: company.id,
															}),
														),
													)
												} else {
													// add
													await mutateOk(
														zero.mutate(
															mutators.contacts.update({
																id: contact.id,
																companyId: company.id,
															}),
														),
													)
												}
											}}
											trigger={
												<Button Icon={Plus} variant="outline">
													Add contact
												</Button>
											}
										/>
									)}
								</RoleCheck>
							</div>

							{isSupportPortalPreview && (
								<div className="mx-6 mt-4">
									<Alert variant="info">
										This is how the support portal looks to{" "}
										<span className="font-semibold">
											{company.domains[0] ?? company.name}
										</span>
										.
									</Alert>
								</div>
							)}

							<TabsContent value="requests" className="mt-4">
								{isSupportPortalPreview ? (
									<div className="px-6">
										<SupportPortalRequestsPreview
											companyId={id}
											isDueDateEnabled={workspace?.isDueDateEnabled ?? false}
										/>
									</div>
								) : (
									<>
										<GroupedIssuesProjectsTable
											categorizedIssues={categorizedIssues}
											categorizedProjects={categorizedProjects}
										/>
										{(Object.values(categorizedProjects).some(
											(arr) => arr.length > 0,
										) ||
											Object.values(categorizedIssues).some(
												(arr) => arr.length > 0,
											)) &&
											threadsState.size > 0 && (
												<div className="my-6 flex w-full items-center justify-center px-6">
													<div className="w-full border-t border-gray-200 dark:border-gray-700/80" />
												</div>
											)}

										<DetailThreadsList
											companyId={id}
											setThreadsSize={threadsState.report}
										/>

										{isEmptyState && (
											<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
												<Empty
													text="No customer requests yet. Link issues or projects to this customer in the thread, or sync them from Linear."
													title="Customer requests"
													className="max-w-[20rem]"
													variant="left"
													image={
														<img
															src={EmptyIssue}
															alt="Empty customer requests"
														/>
													}
													action={
														<div className="flex items-center gap-4">
															<Button
																onClick={() => {
																	void navigate({ to: "/threads" })
																}}
																className="min-w-32"
															>
																Go to Inbox
															</Button>
															<HowItWorksButton
																className="min-h-7"
																article="7e861235-26a4-4b00-9268-a0e82258bbe6"
															/>
														</div>
													}
												/>
											</div>
										)}
									</>
								)}
							</TabsContent>
							<TabsContent value="people" className="mx-6 mt-8">
								<div className="">
									{companyContacts.length === 0 &&
										isQuerySettled(companyResult) && (
											<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
												<Empty
													text="Associate new contacts with this company to keep everything connected in one place."
													title="Customer requests"
													className="max-w-[20rem]"
													variant="left"
													image={
														<img
															src={EmptyIssue}
															alt="Empty customer requests"
														/>
													}
													action={
														<div className="flex items-center gap-4">
															<RoleCheck not={["VIEWER"]}>
																<CompanyContactSelect
																	contactIds={companyContacts.map((c) => c.id)}
																	onSelect={async (contact) => {
																		if (
																			companyContacts
																				.map((c) => c.id)
																				.includes(contact.id)
																		) {
																			// remove
																			await mutateOk(
																				zero.mutate(
																					mutators.contacts.unlinkCompany({
																						contactId: contact.id,
																						companyId: company.id,
																					}),
																				),
																			)
																		} else {
																			// add
																			await mutateOk(
																				zero.mutate(
																					mutators.contacts.update({
																						id: contact.id,
																						companyId: company.id,
																					}),
																				),
																			)
																		}
																	}}
																	trigger={
																		<Button Icon={Plus} className="min-w-36">
																			Add contact
																		</Button>
																	}
																/>
															</RoleCheck>
															<HowItWorksButton
																className="min-h-7"
																article="7e861235-26a4-4b00-9268-a0e82258bbe6"
															/>
														</div>
													}
												/>
											</div>
										)}

									{companyContacts.length > 0 && (
										<div className="space-y-2 pb-10">
											{companyContacts.map((c) => (
												<ContactTag
													key={c.id}
													contact={c}
													onUnlink={async () => {
														// Drops this membership; a contact may work with
														// other customers and must keep those.
														await mutateOk(
															zero.mutate(
																mutators.contacts.unlinkCompany({
																	contactId: c.id,
																	companyId: company.id,
																}),
															),
														)
													}}
												/>
											))}
										</div>
									)}
								</div>
							</TabsContent>
							<TabsContent value="upvotes">
								<DetailUpvotesList
									companyId={id}
									hideLabel
									setThreadsSize={upvotesState.report}
								/>

								{!isEmptyState && upvotesState.isEmpty && (
									<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
										<Empty
											text="This customer did not upvote any issue or project on your feedback portal."
											title="Upvote"
											className="max-w-[20rem]"
											variant="left"
											image={<img src={EmptyIssue} alt="Empty upvotes" />}
											action={
												<div className="flex items-center gap-4">
													<Button
														onClick={() => {
															void navigate({ to: "/edit-requests" })
														}}
														className="min-w-32"
													>
														Go to Requests
													</Button>
													<HowItWorksButton
														className="min-h-7"
														article="bb5ca077-16c1-4d97-811e-c3107d2f1e87"
													/>
												</div>
											}
										/>
									</div>
								)}
							</TabsContent>
						</Tabs>
					</DetailScreenEditor>
				</DetailScreenLayout>
			</DetailScreenWithConnectors>
		</>
	)
}

const SupportPortalRequestsPreview = ({
	companyId,
	isDueDateEnabled,
}: {
	companyId: string
	isDueDateEnabled: boolean
}) => {
	const { data, isLoading } = api.portal.getCompanyPortalRequests.useQuery({
		companyId,
	})

	return (
		<PortalRequestsPreview
			requests={data?.requests ?? []}
			isLoading={isLoading}
			isDueDateEnabled={isDueDateEnabled}
			emptyText="When this company's contacts submit requests or upvote items, they appear here."
		/>
	)
}

const EmptyCustomersDetailsScreen = () => {
	return (
		<div className="flex min-h-0 w-full flex-1 items-center justify-center">
			<Empty
				text="The customer might have been removed or you no longer have access."
				title="Customer not found"
				className="max-w-[20rem]"
				variant="center"
				image={<img src={EmptyCompany} alt="Customer not found" />}
			/>
		</div>
	)
}
